import { Box, FormControl, FormHelperText, FormLabel, TextField } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import { muiInputFieldBorderStyle } from '../../../../constants';
import { useId } from '../../../../utils/useId';

export const DESCRIPTION_MAX_LENGTH = 1500;

interface CollectionDescriptionProps {
	inputRef: React.Ref<HTMLInputElement>;
	error?: FieldError;
	initialValue?: string;
}

const CollectionDescription = ({ inputRef, error, initialValue }: CollectionDescriptionProps) => {
	const { t } = useTranslation();
	const [description, setDescription] = useState(initialValue || '');
	const fieldId = useId('description');
	const labelId = `${fieldId}-label`;
	const errorId = `${fieldId}-error`;
	const characterCountId = `${fieldId}-character-count`;
	const helperTextId = error ? errorId : undefined;
	const describedBy = [helperTextId, characterCountId].filter(Boolean).join(' ') || undefined;

	return (
		<Box data-testid="collection-description-container">
			<FormLabel id={labelId} htmlFor={fieldId} sx={{ color: palette.text.primary }}>
				{t('Description')}
			</FormLabel>
			<FormControl fullWidth>
				<TextField
					id={fieldId}
					fullWidth
					size="medium"
					variant="outlined"
					data-testid="collection-description-input"
					multiline
					minRows={4}
					placeholder={t('What is your collection about?')}
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					inputProps={{
						maxLength: DESCRIPTION_MAX_LENGTH,
						'aria-labelledby': labelId,
						'aria-describedby': describedBy,
						'aria-invalid': !!error || undefined,
					}}
					sx={{
						...muiInputFieldBorderStyle,
					}}
					name="description"
					inputRef={inputRef}
					error={!!error}
					helperText={error?.message}
					FormHelperTextProps={{
						id: helperTextId,
						role: error ? 'alert' : undefined,
					}}
				/>
				<FormHelperText
					id={characterCountId}
					sx={{
						color: palette.text.secondary,
						paddingTop: spaces.xxs,
						paddingRight: spaces.sm,
					}}
					role="status"
					aria-live={DESCRIPTION_MAX_LENGTH - description.length < 50 ? 'assertive' : 'polite'}
					aria-atomic="true"
				>
					{t('<remaining> characters remaining', {
						remaining: DESCRIPTION_MAX_LENGTH - description.length,
					})}
				</FormHelperText>
			</FormControl>
		</Box>
	);
};

export default CollectionDescription;
